import React, { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAuthContext } from '../hooks/useAuthContext'
import { updateUser } from '../helper/userHelper'
import { Plug, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AUTH_INPUT_CLASS } from '@/components/auth/AuthPageShell'
import { cn } from '@/lib/utils'

interface OutletFormProps {
    trigger: boolean
    setOutletPopup: React.Dispatch<React.SetStateAction<boolean>>
    addDeviceHandler: () => void | Promise<void>
}

const OUTLET_FORM_PORTAL_ID = 'hydro-outlet-form-portal'

function getOrCreateOutletFormPortalRoot(): HTMLElement | null {
    if (typeof document === 'undefined') return null
    let el = document.getElementById(OUTLET_FORM_PORTAL_ID)
    if (!el) {
        el = document.createElement('div')
        el.id = OUTLET_FORM_PORTAL_ID
        document.documentElement.appendChild(el)
    }
    return el
}

const OutletForm = ({ trigger, setOutletPopup, addDeviceHandler }: OutletFormProps) => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const { user } = useAuthContext()
    const [outletPortalEl] = useState<HTMLElement | null>(() =>
        typeof document !== 'undefined' ? getOrCreateOutletFormPortalRoot() : null
    )

    /** Same as device settings: fixed overlay + scrolled page needs body freeze and portal on documentElement. */
    useLayoutEffect(() => {
        if (!trigger) return
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0
        const body = document.body
        const html = document.documentElement
        const prev = {
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            width: body.style.width,
            overflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
        }
        body.style.position = 'fixed'
        body.style.top = `-${scrollY}px`
        body.style.left = '0'
        body.style.right = '0'
        body.style.width = '100%'
        body.style.overflow = 'hidden'
        html.style.overflow = 'hidden'

        return () => {
            body.style.position = prev.position
            body.style.top = prev.top
            body.style.left = prev.left
            body.style.right = prev.right
            body.style.width = prev.width
            body.style.overflow = prev.overflow
            html.style.overflow = prev.htmlOverflow
            window.scrollTo(0, scrollY)
        }
    }, [trigger])

    const closeWindow = () => {
        setOutletPopup(false)
    }

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault()
        if (user) {
            user.device_credentials['etekcity'] = {
                email: email,
                password: password,
            }
            user.is_outlet = true
            user.device_to_add = 'etekcity'
            const response = await updateUser(user)
            if (response.ok) {
                console.log('user updated')
                await Promise.resolve(addDeviceHandler())
                setOutletPopup(false)
            } else {
                console.log('user failed update')
                alert('Error adding outlet')
            }
        }
    }

    if (!trigger || !outletPortalEl) {
        return null
    }

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4"
            style={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                minHeight: '100svh',
            }}
            role="presentation"
        >
            <button
                type="button"
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                aria-label="Close dialog"
                onClick={closeWindow}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="outlet-form-title"
                className={cn(
                    'relative z-10 flex max-h-[min(92dvh,640px)] w-full flex-col overflow-hidden rounded-t-2xl border border-zinc-700 bg-zinc-900 text-zinc-100 shadow-2xl sm:max-w-md sm:rounded-2xl'
                )}
                onClick={(ev) => ev.stopPropagation()}
            >
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-zinc-800 px-4 py-4 sm:px-5">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/35">
                            <Plug className="size-5" aria-hidden />
                        </div>
                        <div className="min-w-0">
                            <h2 id="outlet-form-title" className="truncate text-lg font-semibold tracking-tight text-zinc-50">
                                Etekcity outlet account
                            </h2>
                            <p className="mt-0.5 text-sm leading-snug text-zinc-400">
                                Enter credentials used for your smart outlet app.
                            </p>
                        </div>
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                        onClick={closeWindow}
                        aria-label="Close"
                    >
                        <X className="size-5" />
                    </Button>
                </div>

                <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-5">
                    <form className="flex flex-col gap-4 pb-2" onSubmit={(e) => void handleSave(e)}>
                        <div className="space-y-2">
                            <Label htmlFor="outlet-email" className="text-zinc-300">
                                Email
                            </Label>
                            <Input
                                id="outlet-email"
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={AUTH_INPUT_CLASS}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="outlet-password" className="text-zinc-300">
                                Password
                            </Label>
                            <Input
                                id="outlet-password"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={AUTH_INPUT_CLASS}
                            />
                        </div>
                        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full border-zinc-600 bg-zinc-900 text-zinc-100 hover:bg-zinc-800 sm:w-auto"
                                onClick={closeWindow}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" className="w-full bg-emerald-600 font-semibold text-white hover:bg-emerald-500 sm:w-auto">
                                Save
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        outletPortalEl
    )
}

export default OutletForm
